import { useEffect, useState } from 'react';
import AddPaymentMethodModal from '@features/AddPaymentMethodModal/ui/AddPaymentMethodModal';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { FormLabel, Stack, Typography, useMediaQuery } from '@mui/material';
import MainCard from '@components/ui/cards/MainCard';
import Divider from '@mui/material/Divider';
import { useTheme } from '@mui/material/styles';
import BillingHistoryTable from '@features/settings-billing/components/BillingHistoryTable';
import { PaymentMethod, PaymentMethodResponse } from '@app/types/payment';
import useDataFetcher from '@hooks/useDataFetcher/useDataFetcher';
import { urls } from '@api/urls.ts';
import BillingPaymentMethods from '@features/settings-billing/components/BillingPaymentMethods';

function SettingsBillingManager() {
  const theme = useTheme();
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [addPaymentMethodModalOpened, setAddPaymentMethodModalOpened] =
    useState(false);
  const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const {
    data,
    isLoading: isPaymentMethodsLoading,
    mutate: refreshPaymentMethods,
  } = useDataFetcher<PaymentMethodResponse>({
    url: urls.caseSubscription.paymentMethods,
    revalidateOnFocus: false,
  });

  useEffect(() => {
    if (data) {
      setPaymentMethods(data.formatted_payments_methods);
    }
  }, [data]);

  return (
    <MainCard>
      <Typography variant="h6" gutterBottom>
        Billing
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Manage your billing information and payment methods here.
      </Typography>
      <Divider sx={{ marginBottom: 3 }} />
      <Stack spacing={3} divider={<Divider />}>
        <Stack
          direction={isMobile ? 'column' : 'row'}
          alignItems={isMobile ? 'flex-start' : 'center'}
          spacing={2}
        >
          <FormLabel
            sx={{
              minWidth: isMobile ? '100%' : '314px',
              mb: isMobile ? 1 : 0,
            }}
          >
            Payment method
          </FormLabel>
          <BillingPaymentMethods
            isPaymentMethodsLoading={isPaymentMethodsLoading}
            paymentMethods={paymentMethods}
            onAddPaymentMethod={() => setAddPaymentMethodModalOpened(true)}
            refreshPaymentMethods={refreshPaymentMethods}
          />
        </Stack>

        <Stack direction={isMobile ? 'column' : 'row'} spacing={2}>
          <FormLabel
            sx={{
              minWidth: isMobile ? '100%' : '314px',
              mb: 2,
            }}
          >
            Billing history
          </FormLabel>

          <BillingHistoryTable />
        </Stack>
      </Stack>
      <Elements stripe={stripePromise}>
        {addPaymentMethodModalOpened && (
          <AddPaymentMethodModal
            showModal={addPaymentMethodModalOpened}
            onCancel={() => setAddPaymentMethodModalOpened(false)}
            onConfirm={async () => {
              await refreshPaymentMethods();
              setAddPaymentMethodModalOpened(false);
            }}
          />
        )}
      </Elements>
    </MainCard>
  );
}

export default SettingsBillingManager;
